<template>
  <PrimevueDialog
    v-model:visible="dialogVisible"
    :modal="true"
    :style="{ width: '980px', maxWidth: '96vw' }"
    header="Notifications"
  >
    <div class="notification-dialog">
      <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <small class="text-muted">Messages système, alertes et informations liées au site courant.</small>

        <div class="d-flex flex-wrap gap-2">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            :disabled="isLoading || isLoadingMore"
            title="Actualiser la liste"
            aria-label="Actualiser la liste"
            @click="refreshNotifications"
          >
            <i class="fa fa-rotate-right"></i>
            <span class="btn-label ms-1">Actualiser</span>
          </button>

          <button
            v-if="canViewWebDiagnostics"
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            :disabled="isLoadingDiagnostics"
            title="Vérifier les notifications web"
            @click="refreshWebDiagnostics"
          >
            <i class="fa fa-stethoscope"></i>
            <span class="btn-label ms-1">Diagnostic web</span>
          </button>

          <button
            type="button"
            class="btn btn-outline-primary btn-sm btn-icon-label"
            :disabled="isSubmitting"
            @click="markAllAsRead"
          >
            <i class="fa fa-check-double"></i>
            <span class="btn-label ms-1">Tout lire</span>
          </button>
          <button
            v-if="canCreateNotification"
            type="button"
            class="btn btn-primary btn-sm btn-icon-label"
            @click="openCreateDialog"
          >
            <i class="fa fa-plus"></i>
            <span class="btn-label ms-1">Nouvelle notification</span>
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2">{{ errorMessage }}</div>
      <div v-if="successMessage" class="alert alert-success py-2">{{ successMessage }}</div>

      <div v-if="canViewWebDiagnostics" class="notification-web-diagnostics mb-3">
        <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
          <strong>
            <i class="fa fa-bell me-1"></i>
            Notifications web
          </strong>
          <span class="badge" :class="webDiagnosticsReady ? 'bg-success' : 'bg-warning text-dark'">
            {{ webDiagnosticsReady ? 'Prêtes' : 'À vérifier' }}
          </span>
        </div>
        <div class="notification-diagnostic-grid">
          <span v-for="item in webDiagnosticItems" :key="item.label" class="notification-diagnostic-item">
            <small>{{ item.label }}</small>
            <strong :class="item.ok ? 'text-success' : 'text-muted'">{{ item.value }}</strong>
          </span>
        </div>
        <small v-if="webDiagnostics?.missingConfigKeys?.length" class="text-muted d-block mt-2">
          Configuration Firebase manquante : {{ webDiagnostics.missingConfigKeys.join(', ') }}.
        </small>
        <small v-if="webDiagnostics?.lastError" class="text-danger d-block mt-2">
          {{ webDiagnostics.lastError }}
        </small>
      </div>

      <div v-if="isLoading" class="notification-list" aria-live="polite">
        <div v-for="index in 4" :key="`notification-skeleton-${index}`" class="notification-card skeleton">
          <span class="notification-icon placeholder-glow">
            <span class="placeholder col-12"></span>
          </span>
          <div class="notification-card-content placeholder-glow">
            <span class="placeholder col-5 mb-2"></span>
            <span class="placeholder col-10 mb-2"></span>
            <span class="placeholder col-3"></span>
          </div>
        </div>
      </div>

      <div v-else-if="!notifications.length" class="notification-empty">
        <i class="fa fa-bell-slash"></i>
        <strong>Aucune notification</strong>
        <span>Les nouveaux messages système et alertes apparaîtront ici.</span>
      </div>

      <div v-else class="notification-list">
        <article
          v-for="notification in notifications"
          :key="notification.id"
          class="notification-card"
          :class="{ unread: !notification.read_at }"
          role="button"
          tabindex="0"
          @click="openNotification(notification)"
          @keydown.enter="openNotification(notification)"
          @keydown.space.prevent="openNotification(notification)"
        >
          <span class="notification-dot" :class="{ unread: !notification.read_at }"></span>

          <span class="notification-icon" :class="typeToneClass(notification.type)">
            <i :class="typeIcon(notification.type)"></i>
          </span>

          <div class="notification-card-content">
            <div class="d-flex flex-wrap align-items-center gap-2 mb-1">
              <h6 class="mb-0" :class="{ 'fw-bold': !notification.read_at }">
                {{ notification.title }}
              </h6>
              <span class="badge" :class="typeClass(notification.type)">{{ typeLabel(notification.type) }}</span>
              <span class="badge bg-light text-dark notification-site-badge">
                {{ notificationSiteLabel(notification) }}
              </span>
              <span v-if="notification.read_at" class="badge notification-status-badge read">Lue</span>
              <span v-else class="badge notification-status-badge unread">Non lue</span>
            </div>

            <p class="notification-preview mb-2">
              {{ notificationPreview(notification.body) }}
            </p>

            <small class="text-muted">
              <i class="fa fa-clock me-1"></i>
              {{ $format.datetime(notification.created_at) }}
            </small>
          </div>

          <div class="notification-card-actions">
            <button
              v-if="!notification.read_at"
              type="button"
              class="btn btn-outline-primary btn-sm"
              title="Marquer comme lue"
              aria-label="Marquer comme lue"
              @click.stop="markAsRead(notification)"
            >
              <i class="fa fa-check"></i>
            </button>
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm"
              title="Archiver"
              aria-label="Archiver"
              @click.stop="archiveNotification(notification)"
            >
              <i class="fa fa-box-archive"></i>
            </button>
          </div>
        </article>
      </div>

      <div v-if="!isLoading && (hasMoreNotifications || isLoadingMore)" class="notification-load-more mt-3">
        <div v-if="isLoadingMore" class="progress notification-loading-progress" role="progressbar" aria-label="Chargement de nouvelles notifications">
          <div class="progress-bar progress-bar-striped progress-bar-animated w-100"></div>
        </div>
        <button
          type="button"
          class="btn btn-outline-primary btn-sm"
          :disabled="isLoadingMore"
          @click="loadMoreNotifications"
        >
          <i class="fa me-1" :class="isLoadingMore ? 'fa-spinner fa-spin' : 'fa-chevron-down'"></i>
          {{ isLoadingMore ? 'Chargement...' : 'Charger plus' }}
        </button>
      </div>
    </div>

    <PrimevueDialog
      v-model:visible="detailDialogVisible"
      :modal="true"
      :style="{ width: '640px', maxWidth: '96vw' }"
      header="Détail notification"
    >
      <div v-if="selectedNotification">
        <div class="d-flex justify-content-between align-items-start gap-3 mb-2">
          <div>
            <h5 class="mb-1">{{ selectedNotification.title }}</h5>
            <small class="text-muted">{{ $format.datetime(selectedNotification.created_at) }}</small>
          </div>
          <span class="badge" :class="typeClass(selectedNotification.type)">
            {{ typeLabel(selectedNotification.type) }}
          </span>
        </div>

        <div class="mb-3">
          <span class="badge bg-light text-dark notification-site-badge">
            {{ notificationSiteLabel(selectedNotification) }}
          </span>
        </div>

        <p class="mb-3 notification-body">{{ selectedNotification.body || 'Aucun message complémentaire.' }}</p>

        <pre v-if="canViewNotificationData && hasNotificationData" class="notification-data mb-0">{{ formattedNotificationData }}</pre>

        <div class="d-flex justify-content-end gap-2 mt-3">
          <button
            v-if="!selectedNotification.read_at"
            type="button"
            class="btn btn-outline-primary btn-sm btn-icon-label"
            @click="markAsRead(selectedNotification)"
          >
            <i class="fa fa-check"></i>
            <span class="btn-label ms-1">Marquer comme lue</span>
          </button>
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            @click="archiveNotification(selectedNotification)"
          >
            <i class="fa fa-box-archive"></i>
            <span class="btn-label ms-1">Archiver</span>
          </button>
        </div>
      </div>
    </PrimevueDialog>

    <PrimevueDialog
      v-model:visible="createDialogVisible"
      :modal="true"
      :closable="false"
      :style="{ width: '640px', maxWidth: '96vw' }"
    >
      <template #header>
        <h5 class="mb-0">Nouvelle notification</h5>
      </template>

      <div class="row g-3">
        <div class="col-md-6">
          <label class="form-label small fw-semibold">Type</label>
          <PrimevueDropdown
            v-model="form.type"
            :options="typeOptions"
            optionLabel="label"
            optionValue="value"
            class="w-100"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label small fw-semibold">Destinataire</label>
          <PrimevueDropdown
            v-model="form.user_id"
            :options="recipientOptions"
            optionLabel="label"
            optionValue="value"
            class="w-100"
          />
        </div>
        <div class="col-12">
          <label class="form-label small fw-semibold">Titre *</label>
          <PrimevueInputText v-model="form.title" class="w-100" />
        </div>
        <div class="col-12">
          <label class="form-label small fw-semibold">Message</label>
          <PrimevueTextarea v-model="form.body" class="w-100" rows="4" autoResize />
        </div>
      </div>

      <template #footer>
        <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isSubmitting" @click="closeCreateDialog">
          <i class="fa fa-xmark me-1" aria-hidden="true"></i>
          Fermer
        </button>
        <button type="button" class="btn btn-primary btn-sm btn-icon-label" :disabled="isSubmitting" @click="createNotification">
          <i class="fa fa-paper-plane"></i>
          <span class="btn-label ms-1">Envoyer</span>
        </button>
      </template>
    </PrimevueDialog>
  </PrimevueDialog>
</template>

<script>
import {
  hasPermission,
  isSuperAdminUser,
  isSupportUser,
} from '@/support/permissions';
import webNotifications from '@/services/webNotifications';

export default {
  name: 'NotificationsDialog',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:visible', 'changed'],
  data() {
    return {
      notifications: [],
      currentPage: 0,
      hasMoreNotifications: false,
      users: [],
      selectedNotification: null,
      isLoading: false,
      isLoadingMore: false,
      isSubmitting: false,
      errorMessage: '',
      successMessage: '',
      webDiagnostics: null,
      isLoadingDiagnostics: false,
      detailDialogVisible: false,
      createDialogVisible: false,
      form: {
        title: '',
        body: '',
        type: 'info',
        user_id: null,
      },
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      },
    },
    currentUser() {
      return this.$store.getters['auth/currentUser'];
    },
    canCreateNotification() {
      return hasPermission(this.currentUser, 'notifications.create');
    },
    canViewWebDiagnostics() {
      return isSuperAdminUser(this.currentUser) || isSupportUser(this.currentUser);
    },
    canViewNotificationData() {
      return isSuperAdminUser(this.currentUser);
    },
    hasNotificationData() {
      const data = this.selectedNotification?.data;
      return data && Object.keys(data).length > 0;
    },
    formattedNotificationData() {
      if (!this.hasNotificationData) return '';
      return JSON.stringify(this.selectedNotification.data, null, 2);
    },
    typeOptions() {
      return [
        { label: 'Information', value: 'info' },
        { label: 'Alerte', value: 'warning' },
      ];
    },
    recipientOptions() {
      return [
        { label: 'Tout le site', value: null },
        ...this.users.map((user) => ({
          label: `${user.name} - ${user.role}`,
          value: user.id,
        })),
      ];
    },
    currentShopName() {
      return this.$store.getters['shop/shopName'] || 'Site courant';
    },
    webDiagnosticsReady() {
      return Boolean(
        this.webDiagnostics?.available
          && this.webDiagnostics?.configured
          && this.webDiagnostics?.permission === 'granted'
          && this.webDiagnostics?.secureContext
          && this.webDiagnostics?.serviceWorkerSupported
          && this.webDiagnostics?.firebaseServiceWorkerRegistered
          && this.webDiagnostics?.fcmTokenAvailable
      );
    },
    webDiagnosticItems() {
      const diagnostics = this.webDiagnostics;
      const permission = diagnostics?.permission || webNotifications.permission();

      return [
        {
          label: 'Permission',
          value: this.permissionLabel(permission),
          ok: permission === 'granted',
        },
        {
          label: 'Contexte sécurisé',
          value: diagnostics?.secureContext ? 'Oui' : 'Non',
          ok: Boolean(diagnostics?.secureContext),
        },
        {
          label: 'Configuration Firebase',
          value: diagnostics?.configured ? 'Complète' : 'Incomplète',
          ok: Boolean(diagnostics?.configured),
        },
        {
          label: 'Service worker Firebase',
          value: diagnostics?.firebaseServiceWorkerRegistered ? 'Enregistré' : 'Absent',
          ok: Boolean(diagnostics?.firebaseServiceWorkerRegistered),
        },
        {
          label: 'Token FCM',
          value: diagnostics?.fcmTokenAvailable ? 'Disponible' : 'Absent',
          ok: Boolean(diagnostics?.fcmTokenAvailable),
        },
        {
          label: 'Site courant',
          value: this.currentShopName,
          ok: true,
        },
      ];
    },
  },
  watch: {
    visible(value) {
      if (value) {
        this.open();
      }
    },
  },
  mounted() {
    if (this.visible) {
      this.open();
    }
  },
  methods: {
    open() {
      this.refreshNotifications();
      if (this.canViewWebDiagnostics) this.refreshWebDiagnostics();
      if (this.canCreateNotification) this.fetchUsers();
    },
    async refreshWebDiagnostics() {
      if (!this.canViewWebDiagnostics) {
        this.webDiagnostics = null;
        return;
      }

      this.isLoadingDiagnostics = true;
      try {
        this.webDiagnostics = await webNotifications.diagnostics();
      } catch (error) {
        this.webDiagnostics = {
          available: false,
          configured: false,
          missingConfigKeys: [],
          permission: webNotifications.permission(),
          secureContext: false,
          serviceWorkerSupported: false,
          firebaseServiceWorkerRegistered: false,
          firebaseServiceWorkerScope: '',
          fcmTokenAvailable: false,
          deviceUuid: null,
          origin: '',
          lastError: error instanceof Error ? error.message : String(error),
        };
      } finally {
        this.isLoadingDiagnostics = false;
      }
    },
    async fetchNotificationPage(page) {
      const response = await this.$api.get('/notifications', {
        params: {
          paginate: 1,
          page,
          per_page: 5,
          sort_by: 'created_at',
          sort_dir: 'desc',
        },
      });
      const notifications = Array.isArray(response.data?.data) ? response.data.data : [];
      const pagination = response.data?.meta?.pagination || {};

      return { notifications, pagination };
    },
    mergeNotifications(currentNotifications, newNotifications) {
      const knownIds = new Set(currentNotifications.map((notification) => String(notification.id)));
      return [
        ...currentNotifications,
        ...newNotifications.filter((notification) => !knownIds.has(String(notification.id))),
      ];
    },
    async refreshNotifications() {
      return this.fetchNotifications({ pages: 1 });
    },
    async reloadVisibleNotifications() {
      const pages = Math.max(1, Math.ceil(this.notifications.length / 5));
      return this.fetchNotifications({ pages });
    },
    async fetchNotifications({ pages = 1 } = {}) {
      if (this.isLoading || this.isLoadingMore) return;
      this.isLoading = true;
      this.errorMessage = '';
      try {
        let notifications = [];
        let pagination = {};
        for (let page = 1; page <= pages; page += 1) {
          const result = await this.fetchNotificationPage(page);
          notifications = this.mergeNotifications(notifications, result.notifications);
          pagination = result.pagination;
          if (Number(pagination.current_page || page) >= Number(pagination.last_page || page)) break;
        }
        this.notifications = notifications;
        this.currentPage = Number(pagination.current_page || 1);
        this.hasMoreNotifications = this.currentPage < Number(pagination.last_page || this.currentPage);
      } catch (error) {
        this.errorMessage = 'Impossible de charger les notifications.';
      } finally {
        this.isLoading = false;
      }
    },
    async loadMoreNotifications() {
      if (this.isLoading || this.isLoadingMore || !this.hasMoreNotifications) return;
      this.isLoadingMore = true;
      this.errorMessage = '';
      try {
        const nextPage = this.currentPage + 1;
        const { notifications, pagination } = await this.fetchNotificationPage(nextPage);
        this.notifications = this.mergeNotifications(this.notifications, notifications);
        this.currentPage = Number(pagination.current_page || nextPage);
        this.hasMoreNotifications = this.currentPage < Number(pagination.last_page || this.currentPage);
      } catch (error) {
        this.errorMessage = 'Impossible de charger davantage de notifications.';
      } finally {
        this.isLoadingMore = false;
      }
    },
    async fetchUsers() {
      try {
        const response = await this.$api.get('/users', { params: { only_active: 1 } });
        this.users = response.data.data || [];
      } catch (error) {
        this.users = [];
      }
    },
    openNotification(notification) {
      this.selectedNotification = notification;
      this.detailDialogVisible = true;
    },
    notificationPreview(body) {
      if (!body) return 'Aucun message complémentaire.';
      const text = String(body).replace(/\s+/g, ' ').trim();
      return text.length > 150 ? `${text.slice(0, 150)}...` : text;
    },
    notificationSiteLabel(notification) {
      const shop = notification?.shop;
      if (shop?.name) {
        return `Site : ${shop.name}`;
      }

      if (notification?.data?.shop_name) {
        return `Site : ${notification.data.shop_name}`;
      }

      return notification?.shop_id || notification?.data?.shop_id ? 'Site identifié' : 'Global';
    },
    permissionLabel(permission) {
      return {
        granted: 'Autorisée',
        denied: 'Bloquée',
        default: 'Non demandée',
        unsupported: 'Non supportée',
      }[permission] || String(permission || '-');
    },
    openCreateDialog() {
      if (!this.canCreateNotification) return;

      this.form = {
        title: '',
        body: '',
        type: 'info',
        user_id: null,
      };
      this.createDialogVisible = true;
    },
    closeCreateDialog() {
      if (this.isSubmitting) return;
      this.createDialogVisible = false;
    },
    async createNotification() {
      this.isSubmitting = true;
      this.errorMessage = '';
      this.successMessage = '';
      try {
        await this.$api.post('/notifications', {
          title: this.form.title,
          body: this.form.body || null,
          type: this.form.type,
          user_id: this.form.user_id || null,
        });
        this.createDialogVisible = false;
        this.successMessage = 'Notification envoyée.';
        this.notifyChanged();
        await this.reloadVisibleNotifications();
      } catch (error) {
        const errors = error.response?.data?.errors;
        this.errorMessage = errors ? Object.values(errors).flat().join(' ') : 'Impossible de créer la notification.';
      } finally {
        this.isSubmitting = false;
      }
    },
    async markAllAsRead() {
      this.isSubmitting = true;
      try {
        await this.$api.post('/notifications/mark-all-read', {}, { skipSuccessToast: true });
        this.successMessage = '';
        this.notifyChanged();
        await this.reloadVisibleNotifications();
      } catch (error) {
        this.errorMessage = 'Impossible de marquer les notifications comme lues.';
      } finally {
        this.isSubmitting = false;
      }
    },
    async markAsRead(notification) {
      await this.$api.post(`/notifications/${notification.id}/read`, {}, { skipSuccessToast: true });
      if (this.selectedNotification?.id === notification.id) {
        this.selectedNotification = {
          ...notification,
          read_at: new Date().toISOString(),
        };
      }
      this.notifyChanged();
      await this.reloadVisibleNotifications();
    },
    async archiveNotification(notification) {
      if (!(await this.$confirmAction(`Archiver la notification "${notification.title}" ?`))) return;

      await this.$api.post(`/notifications/${notification.id}/archive`);
      this.detailDialogVisible = false;
      this.selectedNotification = null;
      this.successMessage = 'Notification archivée.';
      this.notifyChanged();
      await this.reloadVisibleNotifications();
    },
    typeLabel(type) {
      return {
        info: 'Information',
        warning: 'Alerte',
        public_order_request: 'Commande publique',
        stock_transfer_sent: 'Transfert expédié',
        stock_transfer_received: 'Transfert reçu',
        stock_transfer_cancelled: 'Transfert annulé',
        promotion_active: 'Promotion active',
        stock_low: 'Stock bas',
        stock_out: 'Rupture de stock',
        lot_expiry: 'Expiration de lot',
        product_import_completed: 'Import terminé',
        product_import_failed: 'Import échoué',
        support_ticket_resolved: 'Support résolu',
      }[type] || type || 'Information';
    },
    typeClass(type) {
      if (type === 'warning') return 'bg-warning text-dark';
      if (type === 'public_order_request') return 'bg-info text-dark';
      if (type === 'stock_transfer_sent') return 'bg-primary';
      if (type === 'stock_transfer_received') return 'bg-success';
      if (type === 'stock_transfer_cancelled') return 'bg-danger';
      if (type === 'promotion_active') return 'bg-success';
      if (type === 'stock_low') return 'bg-warning text-dark';
      if (type === 'stock_out') return 'bg-danger';
      if (type === 'lot_expiry') return 'bg-warning text-dark';
      if (type === 'product_import_completed') return 'bg-success';
      if (type === 'product_import_failed') return 'bg-danger';
      if (type === 'support_ticket_resolved') return 'bg-success';
      return 'bg-secondary';
    },
    typeToneClass(type) {
      if (type === 'warning') return 'warning';
      if (type === 'public_order_request') return 'primary';
      if (type === 'stock_transfer_sent') return 'primary';
      if (type === 'stock_transfer_received') return 'success';
      if (type === 'stock_transfer_cancelled') return 'danger';
      if (type === 'promotion_active') return 'success';
      if (type === 'stock_low') return 'warning';
      if (type === 'stock_out') return 'danger';
      if (type === 'lot_expiry') return 'warning';
      if (type === 'product_import_completed') return 'success';
      if (type === 'product_import_failed') return 'danger';
      if (type === 'support_ticket_resolved') return 'success';
      return 'secondary';
    },
    typeIcon(type) {
      if (type === 'warning') return 'fa fa-triangle-exclamation';
      if (type === 'public_order_request') return 'fa fa-bag-shopping';
      if (type === 'stock_transfer_sent') return 'fa fa-truck-arrow-right';
      if (type === 'stock_transfer_received') return 'fa fa-boxes-packing';
      if (type === 'stock_transfer_cancelled') return 'fa fa-ban';
      if (type === 'promotion_active') return 'fa fa-tags';
      if (type === 'stock_low') return 'fa fa-box-open';
      if (type === 'stock_out') return 'fa fa-triangle-exclamation';
      if (type === 'lot_expiry') return 'fa fa-calendar-xmark';
      if (type === 'product_import_completed') return 'fa fa-file-circle-check';
      if (type === 'product_import_failed') return 'fa fa-file-circle-xmark';
      if (type === 'support_ticket_resolved') return 'fa fa-headset';
      return 'fa fa-circle-info';
    },
    notifyChanged() {
      this.$emit('changed');
      window.dispatchEvent(new CustomEvent('suite241:notifications-changed'));
    },
  },
};
</script>

<style scoped>
.notification-dialog {
  min-height: min(62vh, 620px);
}

.notification-web-diagnostics {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.85rem;
}

.notification-diagnostic-grid {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(auto-fit, minmax(145px, 1fr));
}

.notification-diagnostic-item {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  display: grid;
  gap: 0.15rem;
  padding: 0.5rem 0.65rem;
}

.notification-diagnostic-item small {
  color: #64748b;
  font-size: 0.72rem;
}

.notification-list {
  display: grid;
  gap: 0.75rem;
}

.notification-card {
  align-items: flex-start;
  background: var(--suite241-card-bg, #ffffff);
  border: 1px solid var(--suite241-card-border, #e5e7eb);
  border-radius: 8px;
  box-shadow: 0 0.25rem 0.9rem rgba(15, 23, 42, 0.05);
  cursor: pointer;
  display: grid;
  gap: 0.8rem;
  grid-template-columns: auto 42px minmax(0, 1fr) auto;
  padding: 0.85rem;
  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.18s ease;
}

.notification-card:hover,
.notification-card:focus {
  border-color: rgba(13, 110, 253, 0.38);
  box-shadow: 0 0.45rem 1.1rem rgba(15, 23, 42, 0.09);
  outline: none;
  transform: translateY(-1px);
}

.notification-card.unread {
  border-color: rgba(13, 110, 253, 0.35);
  box-shadow: 0 0.35rem 1rem rgba(13, 110, 253, 0.08);
}

.notification-card.skeleton {
  cursor: default;
  pointer-events: none;
}

.notification-dot {
  background-color: #adb5bd;
  border-radius: 999px;
  display: inline-block;
  flex: 0 0 8px;
  height: 8px;
  margin-top: 1rem;
  width: 8px;
}

.notification-dot.unread {
  background-color: var(--bs-primary);
  box-shadow: 0 0 0 4px rgba(13, 110, 253, 0.12);
}

.notification-icon {
  align-items: center;
  background: #f1f5f9;
  border-radius: 8px;
  color: #475569;
  display: inline-flex;
  height: 42px;
  justify-content: center;
  width: 42px;
}

.notification-icon.primary {
  background: rgba(13, 110, 253, 0.12);
  color: #0d6efd;
}

.notification-icon.success {
  background: rgba(25, 135, 84, 0.12);
  color: #198754;
}

.notification-icon.warning {
  background: rgba(255, 193, 7, 0.18);
  color: #946200;
}

.notification-icon.danger {
  background: rgba(220, 53, 69, 0.12);
  color: #dc3545;
}

.notification-icon.secondary {
  background: rgba(108, 117, 125, 0.12);
  color: #495057;
}

.notification-card-content {
  min-width: 0;
}

.notification-preview {
  color: #64748b;
  font-size: 0.88rem;
  line-height: 1.45;
}

.notification-card-actions {
  display: flex;
  flex-wrap: nowrap;
  gap: 0.35rem;
}

.notification-card-actions .btn {
  align-items: center;
  display: inline-flex;
  height: 32px;
  justify-content: center;
  padding: 0;
  width: 32px;
}

.notification-status-badge.read {
  background: rgba(25, 135, 84, 0.12);
  color: #198754;
}

.notification-status-badge.unread {
  background: rgba(255, 193, 7, 0.2);
  color: #6d4c00;
}

.notification-site-badge {
  border: 1px solid #dbe4f0;
}

.notification-empty {
  align-items: center;
  background: var(--suite241-card-bg, #ffffff);
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  color: #64748b;
  display: grid;
  gap: 0.45rem;
  justify-items: center;
  padding: 2rem 1rem;
  text-align: center;
}

.notification-empty i {
  color: #94a3b8;
  font-size: 1.55rem;
}

.notification-load-more { align-items: center; display: grid; gap: 0.65rem; justify-items: center; }
.notification-loading-progress { height: 4px; max-width: 420px; width: 100%; }

.notification-body {
  white-space: pre-line;
}

.notification-data {
  background-color: #f8f9fa;
  border-radius: 6px;
  color: #495057;
  font-size: 0.78rem;
  padding: 0.75rem;
}

@media (max-width: 767.98px) {
  .notification-card {
    grid-template-columns: auto 38px minmax(0, 1fr);
  }

  .notification-icon {
    height: 38px;
    width: 38px;
  }

  .notification-card-actions {
    grid-column: 3;
    justify-content: flex-start;
  }
}
</style>
